import {RequestStatus} from "@/api/data-contracts";
import {AppThemeContextProps} from "@/app/AppThemeContext";
import {LabelValue} from "@/lib/components/LabelValueList";
import { CommonI18N } from "@/translations";
import {color_blue, color_green, color_grey, color_orange, color_purple} from "@/agentur/AgenturColors";

interface RequestStatusField {
    requestStatus?: RequestStatus | null;
    statusValue?: string | null;
    submittedQuoteCount?: number | null;
}

/**
 * Generates a field object for the status of a request with a label, value, tooltip, and color.
 *
 * @param {RequestStatusField} status - The status object containing the request status and status value.
 * @param {string} label - The label for the field.
 * @param {AppThemeContextProps} theme - The theme context properties.
 * @param {string} [defaultValue=""] - The default value to use if the status value is not available.
 * @param {string} [tooltipKey] - The key for the tooltip text.
 * @returns {LabelValue} The field object with label, value, tooltipKey, and color properties.
 */
export const requestStatusField = (
    status: RequestStatusField,
    label: string,
    theme: AppThemeContextProps,
    defaultValue?: RequestStatusField,
    tooltipKey?: string
): LabelValue => {
    let value = defaultValue?.statusValue ?? "";

    if (status?.requestStatus && status?.statusValue) {
        value = status.statusValue;

        if (status.requestStatus === RequestStatus.TO_BE_CONFIRMED && !!status.submittedQuoteCount) {
            value = `${status.statusValue}: ${status.submittedQuoteCount}`;
        }
    }

    return {
        label,
        value,
        tooltipKey,
        color: getRequestStatusColor(theme.mode, status.requestStatus ?? undefined)
    };
};
/**
 * Returns the color associated with a given request status.
 *
 * @returns {string} The color corresponding to the given status code.
 * @param mode
 * @param status
 */
export const getRequestStatusColor = (mode: "dark" | "light", status?: RequestStatus): string => {
    switch (status) {
        case RequestStatus.CREATED:
            return color_blue[2];
        case RequestStatus.PUBLISHED:
            return color_green[4];
        case RequestStatus.TO_BE_CONFIRMED:
            return color_purple[mode === "dark" ? 2 : 4];
        case RequestStatus.CONFIRMED:
            return color_green[2];
        case RequestStatus.DEACTIVATED:
            return color_grey[3];
        case RequestStatus.CANCELLED:
            return color_purple[3];
        case RequestStatus.IN_NEGOTIATION:
            return color_blue[3];
        case RequestStatus.DRAFT:
            return color_orange[2];
        default:
            return color_grey[3];
    }
};

export const getRequestStatusText = (t: (key: string) => string, status?: RequestStatus): string =>
    (status && t(CommonI18N.enum.requestStatus[status])) ?? t(CommonI18N.message.notGiven);
